import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { IndosbookLogo } from "./IndosbookLogo";
import { legalPages, footerLegalLinks } from "../mock";
import { ArrowLeft } from "lucide-react";
import { BackToTop } from "./BackToTop";
import { usePageMeta } from "../lib/seo";

const LEGAL_DESCRIPTIONS = {
  terms: "Read the Indosbook Terms & Conditions for seafarers, shipping companies and maritime institutes using the merchant navy social network.",
  privacy: "How Indosbook collects, uses and protects your personal data, sea service records and certificates. Your privacy on the merchant navy network.",
  disclaimer: "Indosbook disclaimer for job listings, courses and member content.",
  cookies: "How Indosbook uses cookies.",
  refund: "Indosbook refund policy.",
};

// map url slug -> legalPages key
const slugMap = {
  terms: "terms",
  privacy: "privacy",
  disclaimer: "disclaimer",
  cookies: "cookies",
  refund: "refund",
};

const LegalPage = () => {
  const { slug } = useParams();
  const key = slugMap[slug] || "terms";
  const page = legalPages[key];

  // always open the page from the very top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // only Privacy and Terms are shown in search results
  usePageMeta({
    title: `${page.title} | Indosbook`,
    description: LEGAL_DESCRIPTIONS[key],
    path: `/legal/${slug}`,
    noindex: !["privacy", "terms"].includes(key),
  });

  return (
    <div className="min-h-screen bg-white">
      <BackToTop />
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[900px] items-center justify-between px-4">
          <Link to="/">
            <IndosbookLogo size={28} />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 transition-colors hover:text-[#FF4500]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <section className="indos-hero-mesh indos-grid-pattern relative overflow-hidden border-b border-stone-200/80">
        <div className="relative mx-auto max-w-[820px] px-5 py-14 md:py-16">
          <p className="text-sm font-semibold text-[#FF4500]">Indosbook • Legal</p>
          <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-extrabold text-gray-900">
            {page.title}
          </h1>
          <p className="mt-2 text-sm text-gray-400">{page.updated}</p>
        </div>
      </section>

      <main className="mx-auto max-w-[820px] px-5 py-12">
        <p className="text-[15px] leading-relaxed text-gray-700">
          {page.intro}
        </p>

        <div className="mt-8 space-y-7">
          {page.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-heading text-lg font-bold text-gray-900">{s.h}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-gray-600">
                {s.p}
              </p>
            </section>
          ))}
        </div>

        {/* jump to other policies */}
        <div className="mt-12 border-t border-gray-100 pt-6">
          <div className="text-sm font-bold text-gray-800">Other policies</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {footerLegalLinks
              .filter((l) => !l.to.endsWith(slug))
              .map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-orange-50 hover:text-[#FF4500]"
                >
                  {l.label}
                </Link>
              ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-gray-100 bg-gray-50 py-8 text-center text-sm text-gray-400">
        © 2026 Indosbook. Built for seafarers, companies and maritime institutes.
      </footer>
    </div>
  );
};

export default LegalPage;
